// src/modules/cv/ColorSpaceLab.jsx
// Color Space Lab — RGB, HSV, YCbCr, Lab — channel decomposition + concepts

import React, { useState, useCallback, useEffect } from 'react';
import { generateSynth, imgToImageData, imageDataToURL, colorSpaceChannels, rgbToHsv, toGray } from '../../utils/cvUtils.js';
import { Panel, InfoBox, Metric } from '../../components/ui.jsx';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const TABS = ['upload', 'rgb', 'hsv', 'ycbcr', 'lab', 'concepts'];
const Btn = ({onClick,children,color='var(--orange)',disabled})=>(<button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</button>);

const SPACES = {
  rgb:   {label:'RGB', color:'var(--red)',    channels:['Red','Green','Blue'],     colors:['var(--red)','var(--green)','var(--blue)']},
  hsv:   {label:'HSV', color:'var(--orange)', channels:['Hue','Saturation','Value'], colors:['#f472b6','var(--orange)','var(--gold)']},
  ycbcr: {label:'YCbCr',color:'var(--cyan)',  channels:['Luma (Y)','Cb (blue diff)','Cr (red diff)'], colors:['var(--t2)','var(--blue)','var(--red)']},
  lab:   {label:'L*a*b*',color:'var(--green)',channels:['L* (lightness)','a* (green↔red)','b* (blue↔yellow)'], colors:['var(--t2)','var(--red)','var(--gold)']},
};

const CONCEPTS = [
  {title:'RGB',color:'var(--red)',desc:'Additive color model used by displays. Each pixel is (R,G,B) in [0,255]. All channels correlated — changing brightness affects all 3. Not perceptually uniform.'},
  {title:'HSV',color:'var(--orange)',desc:'Hue (color wheel 0–360°), Saturation (purity 0–100%), Value (brightness 0–100%). Separates color from brightness — ideal for color-based tracking and selection.'},
  {title:'YCbCr',color:'var(--cyan)',desc:'Y = luma (0.299R + 0.587G + 0.114B). Cb,Cr = color difference signals. Used in JPEG, video compression. Human eyes are more sensitive to Y — more bits allocated to it.'},
  {title:'CIE L*a*b*',color:'var(--green)',desc:'Perceptually uniform — equal numeric distances = equal perceived color differences. L* = lightness, a* = green↔red axis, b* = blue↔yellow axis. Ideal for color comparison and quality control.'},
  {title:'Why Convert?',color:'var(--violet)',desc:'Different spaces excel at different tasks: HSV for color thresholding, YCbCr for compression, Lab for perceptual quality metrics, grayscale for classical CV algorithms that expect single-channel input.'},
  {title:'Color Constancy',color:'var(--gold)',desc:'Real cameras capture different RGB values for the same surface under different lighting. White balance normalizes illumination. Working in Lab or HSV helps separate illumination from surface color.'},
];

function getRGBChannels(id) {
  const {width:w,height:h,data:src} = id;
  const R=new Uint8ClampedArray(src.length),G=new Uint8ClampedArray(src.length),B=new Uint8ClampedArray(src.length);
  for(let i=0;i<src.length;i+=4){R[i]=src[i];R[i+1]=0;R[i+2]=0;R[i+3]=255;G[i]=0;G[i+1]=src[i+1];G[i+2]=0;G[i+3]=255;B[i]=0;B[i+1]=0;B[i+2]=src[i+2];B[i+3]=255;}
  return [new ImageData(R,w,h),new ImageData(G,w,h),new ImageData(B,w,h)];
}

const SYNTH = [{name:'Gradient',type:'gradient'},{name:'Shapes',type:'shapes'},{name:'Portrait',type:'portrait'},{name:'Circles',type:'circles'}];

export default function ColorSpaceLab({ seedImageSrc = null, hideUpload = false }) {
  const [tab, setTab] = useState(hideUpload ? 'rgb' : 'upload');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [channels, setChannels] = useState(null);
  const [space, setSpace] = useState('rgb');

  const loadSrc = useCallback(async (src) => {
    setImgSrc(src);
    const id = await imgToImageData(src);
    setImgData(id);
    setChannels(null);
    if (tab === 'upload') setTab('rgb');
  }, [tab]);

  useEffect(() => {
    if (seedImageSrc) loadSrc(seedImageSrc);
  }, [seedImageSrc, loadSrc]);

  useEffect(() => {
    if (hideUpload && tab === 'upload') setTab('rgb');
  }, [hideUpload, tab]);

  const handleFile = (file) => {
    if (!file?.type.startsWith('image/')) return;
    const r = new FileReader(); r.onload = e => loadSrc(e.target.result); r.readAsDataURL(file);
  };

  const computeChannels = (sp) => {
    if (!imgData) return;
    setSpace(sp);
    if (sp === 'rgb') setChannels(getRGBChannels(imgData));
    else setChannels(colorSpaceChannels(imgData, sp));
    setTab(sp);
  };

  const SpaceTab = ({ sp }) => {
    const meta = SPACES[sp];
    const chs = sp === 'rgb' ? (imgData ? getRGBChannels(imgData) : null) : (imgData ? colorSpaceChannels(imgData, sp) : null);
    return (
      <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
        <Panel title={`${meta.label} — CHANNEL DECOMPOSITION`} accent={meta.color}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'10px'}}>
            <div><div style={{fontSize:'10px',color:'var(--t2)',textAlign:'center',marginBottom:'6px',fontWeight:600}}>Original</div><img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/></div>
            {chs && meta.channels.map((ch,i)=>(
              <div key={ch}><div style={{fontSize:'10px',color:meta.colors[i],textAlign:'center',marginBottom:'6px',fontWeight:600}}>{ch}</div><img src={imageDataToURL(chs[i])} alt={ch} style={{width:'100%',borderRadius:'3px',border:`1px solid ${meta.colors[i]}44`}}/></div>
            ))}
          </div>
        </Panel>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
          {chs && meta.channels.map((ch,i)=>{
            const data=chs[i].data,hist=new Array(16).fill(0),bw=16;
            for(let j=0;j<data.length;j+=4)hist[Math.min(15,Math.floor(data[j]/bw))]++;
            const chartData=hist.map((v,j)=>({bin:j*bw,count:v}));
            return (
              <Panel key={ch} title={ch} accent={meta.colors[i]}>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="bin" stroke="var(--t3)" tick={{fontSize:7}} interval={3}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:7}}/>
                    <Bar dataKey="count" fill={meta.colors[i]} opacity={.8} radius={[1,1,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {(hideUpload ? TABS.filter(t => t !== 'upload') : TABS).map(t=><button key={t} onClick={()=>{if(t==='concepts')setTab(t);else if(t!=='upload')computeChannels(t);else setTab(t);}} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--orange)':'transparent'}`,background:tab===t?'var(--orange)18':'transparent',color:tab===t?'var(--orange)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {tab==='upload' && !hideUpload && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}} onDragOver={e=>e.preventDefault()} onClick={()=>document.getElementById('color-file').click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input id="color-file" type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>◎</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop Image</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Color images work best</div>
          </div>
          <Panel title="SYNTHETIC TEST IMAGES" accent="var(--orange)">
            {SYNTH.map(({name,type})=>(
              <div key={type} onClick={()=>loadSrc(generateSynth(type,256))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 12px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--orange)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>256×256 synthetic</div></div>
                <span style={{color:'var(--orange)'}}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {tab==='rgb' && imgData && <SpaceTab sp="rgb"/>}
      {tab==='hsv' && imgData && <SpaceTab sp="hsv"/>}
      {tab==='ycbcr' && imgData && <SpaceTab sp="ycbcr"/>}
      {tab==='lab' && imgData && <SpaceTab sp="lab"/>}

      {tab==='concepts' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          {CONCEPTS.map(({title,color,desc})=>(
            <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
              <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
              <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
            </div>
          ))}
        </div>
      )}

      {!imgData && tab!=='upload' && tab!=='concepts' && <InfoBox color="var(--orange)" icon="◎">Go to UPLOAD tab to load an image first.</InfoBox>}
    </div>
  );
}
